<script setup>
import Layout from "../../layouts/main.vue";
import MethodService from "../../service/MethodService";
import mushroom from "cem-primary-api";
import { onMounted, reactive, ref, computed } from "vue";
import DataForm from "./dataServiceHealthComponent";
import { FormInstance } from "element-plus";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import toastr from "toastr";
import Swal from "sweetalert2";
import DOMPurify from "dompurify";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const ruleFormRef = ref(FormInstance);
const formRef = ref(null);
const loading = ref(false);
const dialogVisible = ref(false);
const dialogType = ref("add");
const originalName = ref("");

const dialogTitle = computed(() => {
  if (dialogType.value === "add") return t("t_add_component");
  return (
    t("t_edit_component") +
    (originalName.value ? ": " + originalName.value : "")
  );
});

const tableRules = reactive(MethodService.copyObject(DataForm.tableRules));
const validForm = reactive({ value: DataForm.tableRules.dataSearch.valid });
const dataTables = reactive({ value: [] });

const formData = reactive({
  id: null,
  name: "",
  description: "",
  type: null,
  weight: null,
});

const rules = {
  name: [{ required: true, message: t("valid_required"), trigger: "blur" }],
  type: [
    { required: true, message: t("valid_requiredChange"), trigger: "blur" },
  ],
};

const updateUrlQuery = (extraParams = {}) => {
  const query = {
    ...tableRules.dataSearch.value,
    _limit: tableRules.limit,
    _offset: tableRules.offset,
    _sort: tableRules.sort,
    showFormSearch: tableRules.showFormSearch,
    page: tableRules.page,
    ...extraParams,
  };
  router.replace({ query }).catch(() => {});
};

const syncStateFromUrl = () => {
  const q = route.query;
  if (q.limit) tableRules.limit = parseInt(q.limit);
  if (q.page) tableRules.page = parseInt(q.page);
  tableRules.offset = (tableRules.page - 1) * tableRules.limit;

  if (q._sort) {
    tableRules.sort = q._sort;
    tableRules.defaultSort[0] = tableRules.sort.replace("-", "");
    tableRules.defaultSort[1] = tableRules.sort.startsWith("-")
      ? "descending"
      : "ascending";
  }

  if (q.showFormSearch) {
    tableRules.showFormSearch = q.showFormSearch === "true";
  }

  Object.keys(tableRules.dataSearch.value).forEach((key) => {
    if (q[key] !== undefined) tableRules.dataSearch.value[key] = q[key];
  });
};

const getListData = async () => {
  loading.value = true;
  updateUrlQuery();

  const searchParams = {};
  Object.keys(tableRules.dataSearch.value).forEach((key) => {
    const val = tableRules.dataSearch.value[key];
    if (val !== "" && val !== null && val !== undefined) {
      searchParams[key] = val;
    }
  });

  const filter = {
    _limit: tableRules.limit,
    _offset: tableRules.offset,
    _sort: tableRules.sort,
    ...searchParams,
  };

  try {
    const [listRes, countRes] = await Promise.all([
      mushroom.service_health_component.views.getListAsync(filter),
      mushroom.service_health_component.views.getCountAsync(),
    ]);

    dataTables.value = listRes.result || [];
    tableRules.total = countRes.result?.[0]?.total || 0;
  } catch (e) {
    MethodService.showError(e.code);
  } finally {
    loading.value = false;
  }
};

const fn_showFormTimKiem = () => {
  tableRules.showFormSearch = !tableRules.showFormSearch;
  updateUrlQuery({ showFormSearch: tableRules.showFormSearch });
};

const fn_TimKiem = (formEl) => {
  if (!formEl) return;
  formEl.validate((valid) => {
    if (!valid) return;
    Object.keys(tableRules.dataSearch.value).forEach((key) => {
      const val = tableRules.dataSearch.value[key];
      if (typeof val === "string")
        tableRules.dataSearch.value[key] = val.trim();
    });
    tableRules.page = 1;
    tableRules.offset = 0;
    getListData();
  });
};

const fn_tableSortChange = (column) => {
  MethodService.tableSortChange(column, tableRules);
  tableRules.offset = (tableRules.page - 1) * tableRules.limit;
  getListData();
};

const fn_tableSizeChange = (limit) => {
  tableRules.limit = limit;
  tableRules.page = 1;
  tableRules.offset = 0;
  getListData();
};

const fn_tableCurentChange = (page) => {
  tableRules.page = page;
  tableRules.offset = (tableRules.page - 1) * tableRules.limit;
  getListData();
};

const handleAdd = () => {
  dialogType.value = "add";
  originalName.value = "";
  Object.assign(formData, {
    name: "",
    type: null,
    weight: null,
    description: "",
  });
  dialogVisible.value = true;
};

const handleEdit = (row) => {
  dialogType.value = "edit";
  originalName.value = row.name;
  Object.assign(formData, {
    id: row.id,
    name: row.name,
    type: row.type,
    weight: row.weight,
    description: row.description,
  });
  dialogVisible.value = true;
};

const handleDelete = (row) => {
  if (!row.id) return;
  Swal.fire({
    title: t("t_warning"),
    text: t("swal_confirm_delete_component"),
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#f46a6a",
    confirmButtonText: t("swal_accept"),
    cancelButtonText: t("swal_cancel"),
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await mushroom.service_health_component.deleteAsync({
          id: row.id,
        });
        toastr.success(t("toastr_delete_component_success"));
        getListData();
      } catch (e) {
        if (
          e?.code === 400 &&
          e?.subCode === 1003 &&
          e?.meta?.dpi?.data?.length > 0
        ) {
          const serviceIds = [
            ...new Set(e.meta.dpi.data.map((item) => item.service_id)),
          ];
          const serviceResponses = await Promise.all(
            serviceIds.map((id) =>
              mushroom.service_health_ch.findByIdAsync({ id }).catch(() => null),
            ),
          );
          const serviceNames = serviceResponses
            .filter((res) => res && res.result)
            .map((res) => `<li>${res.result.service_name}</li>`)
            .join("");

          const htmlContent = DOMPurify.sanitize(
            `<div class="text-start">${t(
              "toastr_component_in_use_service_error",
            )}</div><ul class="text-start mt-2">${serviceNames}</ul>`,
          );

          Swal.fire({
            title: t("t_warning"),
            html: htmlContent,
            icon: "warning",
            confirmButtonColor: "#34c38f",
            confirmButtonText: t("swal_accept"),
          });
        } else if (e?.code === 400) {
          if (e.subCode === 1003) toastr.error(t("toastr_component_in_use"));
        } else if (e?.code === 403) {
          if (e.subCode === 1101) toastr.error(t("t_component_not_deletable"));
        } else toastr.error(t("toastr_error"));
      }
    }
  });
};

const submitForm = async () => {
  if (!formRef.value) return;

  await formRef.value.validate(async (valid) => {
    if (!valid) return;

    try {
      if (dialogType.value === "add") {
        await mushroom.service_health_component.createAsync(formData);
        toastr.success(t("toastr_add_component_success"));
      } else {
        await mushroom.service_health_component.partialUpdateAsync(formData);
        toastr.success(t("toastr_update_component_success"));
      }

      dialogVisible.value = false;
      getListData();
    } catch (e) {
      const errorMap = {
        1002: "toastr_component_duplicate",
        1003: "toastr_component_in_use",
      };

      const subMsg = e?.code === 400 ? errorMap[e.subCode] : null;

      if (subMsg) {
        toastr.error(t(subMsg));
      } else {
        toastr.error(t("toastr_error"));
      }
    }
  });
};

onMounted(() => {
  syncStateFromUrl();
  getListData();
});
</script>

<template>
  <Layout>
    <div class="card">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <h3 class="text-uppercase mb-0">
          {{ $t("t_service_health_component_management") }}
        </h3>
        <div class="text-end">
          <div class="btn-header">
            <button
              class="btn btn-soft-secondary btn-border me-2"
              @click="fn_showFormTimKiem()"
            >
              <i class="ri-search-line"></i>
              {{ $t("t-search") }}
            </button>
            <button class="btn btn-secondary btn-border" @click="handleAdd">
              <i class="ri-add-circle-line"></i>
              {{ $t("t-add") }}
            </button>
          </div>
        </div>
      </div>

      <div class="card-body p-0 pb-3">
        <div class="main-body">
          <div class="main-body-search mx-3">
            <el-collapse-transition>
              <div v-show="tableRules.showFormSearch">
                <b-card class="b-shadow mb-0">
                  <el-form
                    ref="ruleFormRef"
                    :model="tableRules.dataSearch.value"
                    :rules="validForm.value"
                    label-position="top"
                    @submit.prevent
                  >
                    <b-row align-v="end">
                      <b-col md="3">
                        <el-form-item
                          :label="$t('t_component_name')"
                          prop="name"
                        >
                          <el-input
                            clearable
                            v-model="tableRules.dataSearch.value['name']"
                            :placeholder="$t('t_place_component_name')"
                          ></el-input>
                        </el-form-item>
                      </b-col>
                      <b-col md="3">
                        <el-form-item
                          :label="$t('t_component_type')"
                          prop="type"
                        >
                          <el-select
                            v-model="tableRules.dataSearch.value['type']"
                            :placeholder="$t('t_select_component_type')"
                            clearable
                            style="width: 100%"
                          >
                            <el-option
                              label="Deep Analyze"
                              value="Deep Analyze"
                            />
                            <el-option
                              label="Normal Analyze"
                              value="Normal Analyze"
                            />
                          </el-select>
                        </el-form-item>
                      </b-col>
                      <b-col md="3">
                        <el-form-item :label="$t('t_weight')">
                          <el-input-number
                            v-model="tableRules.dataSearch.value['weight']"
                            :placeholder="$t('t_place_weight')"
                            style="width: 100%"
                            :min="0"
                            :step="1"
                            step-strictly
                            clearable
                            controls-position="right"
                          />
                        </el-form-item>
                      </b-col>
                      <b-col md="3">
                        <el-form-item label="&nbsp;">
                          <div class="d-flex justify-content-center">
                            <a
                              class="btn btn-soft-secondary btn-border me-2"
                              style="width: 80px"
                              @click="fn_showFormTimKiem()"
                            >
                              {{ $t("t-hide") }}
                            </a>
                            <button
                              class="btn btn-secondary bg-gradient btn-border waves-effect waves-light"
                              style="width: 100px"
                              native-type="submit"
                              @click="fn_TimKiem(ruleFormRef)"
                            >
                              {{ $t("t-search") }}
                            </button>
                          </div>
                        </el-form-item>
                      </b-col>
                    </b-row>
                  </el-form>
                </b-card>
              </div>
            </el-collapse-transition>
          </div>

          <div class="d-flex justify-content-end mt-3 mb-3 px-3">
            <el-pagination
              small
              v-model:currentPage="tableRules.page"
              v-model:pageSize="tableRules.limit"
              :page-sizes="tableRules.lengthMenu"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableRules.total"
              @size-change="fn_tableSizeChange"
              @current-change="fn_tableCurentChange"
            />
          </div>

          <el-table
            border
            size="small"
            max-height="520"
            stripe
            :data="dataTables.value"
            style="width: 100%"
            v-loading="loading"
            :empty-text="$t('t-no-data')"
            @sort-change="fn_tableSortChange"
            :default-sort="{
              prop: tableRules.defaultSort[0],
              order: tableRules.defaultSort[1],
            }"
          >
            <el-table-column :label="$t('t-stt')" width="60" align="center">
              <template #default="scope">
                {{
                  scope.$index + (tableRules.page - 1) * tableRules.limit + 1
                }}
              </template>
            </el-table-column>
            <el-table-column
              prop="name"
              :label="$t('t_component_name')"
              align="center"
              min-width="150"
              sortable="custom"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div class="col-tooltip">{{ scope.row.name }}</div>
              </template>
            </el-table-column>
            <el-table-column
              prop="type"
              :label="$t('t_component_type')"
              align="center"
              width="120"
              sortable="custom"
            >
              <template #default="scope">
                <span
                  v-if="scope.row.type === 'Deep Analyze'"
                  class="badge badge-soft-danger"
                  >Deep Analyze</span
                >
                <span
                  v-else-if="scope.row.type === 'Normal Analyze'"
                  class="badge badge-soft-success"
                  >Normal Analyze</span
                >
                <span v-else class="badge badge-soft-info">{{
                  scope.row.type
                }}</span>
              </template>
            </el-table-column>
            <el-table-column
              prop="weight"
              :label="$t('t_weight')"
              align="center"
              width="100"
              sortable="custom"
            />
            <el-table-column
              prop="description"
              :label="$t('t_description')"
              align="center"
              min-width="250"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div class="col-tooltip">{{ scope.row.description }}</div>
              </template>
            </el-table-column>

            <el-table-column
              :label="$t('t-action')"
              width="120"
              align="center"
              fixed="right"
            >
              <template #default="scope">
                <div class="btn-group-thao-tac">
                  <el-tooltip
                    :content="$t('action_option')"
                    effect="light"
                    placement="top"
                  >
                    <button
                      class="btn btn-soft-success waves-effect waves-light btn-sm me-1"
                      @click="handleEdit(scope.row)"
                    >
                      <i class="ri-edit-box-line"></i>
                    </button>
                  </el-tooltip>
                  <el-tooltip
                    :content="$t('action_delete')"
                    effect="light"
                    placement="top"
                  >
                    <button
                      class="btn btn-soft-danger waves-effect waves-light btn-sm"
                      :disabled="scope.row.create_by === 'System'"
                      @click="handleDelete(scope.row)"
                    >
                      <i class="ri-delete-bin-6-line"></i>
                    </button>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>

          <div class="d-flex justify-content-end mt-3 px-3">
            <el-pagination
              small
              v-model:currentPage="tableRules.page"
              v-model:pageSize="tableRules.limit"
              :page-sizes="tableRules.lengthMenu"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableRules.total"
              @size-change="fn_tableSizeChange"
              @current-change="fn_tableCurentChange"
            />
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="500px"
      append-to-body
    >
      <el-form
        ref="formRef"
        :model="formData"
        :rules="rules"
        label-position="top"
      >
        <el-form-item :label="$t('t_component_name')" prop="name">
          <el-input
            v-model="formData.name"
            :placeholder="$t('t_place_component_name')"
          />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="14">
            <el-form-item :label="$t('t_component_type')" prop="type">
              <el-select
                v-model="formData.type"
                :placeholder="$t('t_select_component_type')"
                clearable
                style="width: 100%"
              >
                <el-option label="Deep Analyze" value="Deep Analyze" />
                <el-option label="Normal Analyze" value="Normal Analyze" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="10">
            <el-form-item :label="$t('t_weight')">
              <el-input-number
                v-model="formData.weight"
                :placeholder="$t('t_place_weight')"
                style="width: 100%"
                :min="0"
                :step="1"
                step-strictly
                controls-position="right"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item :label="$t('t_description')">
          <el-input
            v-model="formData.description"
            type="textarea"
            :rows="2"
            :placeholder="$t('t_place_description')"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="text-center">
          <el-button type="primary" @click="submitForm">
            {{ dialogType === "add" ? $t("t-add") : $t("t-update") }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </Layout>
</template>
